import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Container, Dropdown, DropdownButton, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import NavbarComponent from "./Nav";
import { useAuth } from "./store/AuthContext";
import "./CSS/MainPage.css";

import SchoolGenreComponent from "./crudFiles/schoolGenre";
import SchoolTypeComponent from "./crudFiles/schoolType";
import DistrictComponent from "./crudFiles/districts";
import SchoolComponent from "./crudFiles/schools";
import GradeComponent from "./crudFiles/grades";
import TeacherStatusComponent from "./crudFiles/teacherStatus";
import LocationComponent from "./crudFiles/locations";
import UserTypeComponent from "./crudFiles/appUserTypes";
import NewAccountForm from "./crudFiles/appUser";
import ClassroomComponent from "./crudFiles/classroom";
import EventType from "./crudFiles/eventTypes";
import EventTemplateComponent from "./crudFiles/eventTemplate";
import RecordStatusComponent from "./crudFiles/record_status";
import TicketStatusComponent from "./crudFiles/ticketStatus";
import ActionComponent from "./crudFiles/actions";
import ResourceComponent from "./crudFiles/resource";
import PermissionComponent from "./crudFiles/permissions";
import EventsProgramComponent from "./crudFiles/eventPrograms";
import TeacherProfile from "./crudFiles/obcTeacherProfile";
import JudgeProfile from "./crudFiles/judgeProfile";
import UHSMTATeacherProfile from "./crudFiles/uhsmtaTeacherProfile";
import WorkshopTeachersOBC from "./crudFiles/workshopsTeachersOBC";
import SelectSchoolYearComponent from "./crudFiles/yearSelect";
import WorkshopStudentsOBC from "./crudFiles/obcWorkshopStudents";
import TheaterForYoungPeople from "./crudFiles/theaterForYoungPeople";
import SiteContentComponent from "./crudFiles/siteContent";

const MANAGEMENT_MODULES = [
  { id: "schools", label: "Schools", group: "School Information", Component: SchoolComponent },
  { id: "schoolTypes", label: "School Types", group: "School Information", Component: SchoolTypeComponent },
  { id: "schoolGenres", label: "School Genres", group: "School Information", Component: SchoolGenreComponent },
  { id: "districts", label: "Districts", group: "School Information", Component: DistrictComponent },
  { id: "grades", label: "Grades", group: "School Information", Component: GradeComponent },
  { id: "schoolYear", label: "School Year", group: "School Information", Component: SelectSchoolYearComponent },

  { id: "classrooms", label: "Classrooms", group: "OBC", Component: ClassroomComponent },
  { id: "obcTeacherProfiles", label: "OBC Teacher Profiles", group: "OBC", Component: TeacherProfile },
  { id: "teacherStatuses", label: "Teacher Statuses", group: "OBC", Component: TeacherStatusComponent },
  { id: "obcWorkshopTeachers", label: "OBC Workshop Teachers", group: "OBC", Component: WorkshopTeachersOBC },
  { id: "obcWorkshopStudents", label: "OBC Workshop Students", group: "OBC", Component: WorkshopStudentsOBC },

  { id: "judgeProfiles", label: "Judge Profiles", group: "UHSMTA", Component: JudgeProfile },
  { id: "uhsmtaTeachers", label: "UHSMTA Teachers", group: "UHSMTA", Component: UHSMTATeacherProfile },

  { id: "typEvents", label: "TYP Events", group: "Theater for Young People", Component: TheaterForYoungPeople },

  { id: "userTypes", label: "User Types", group: "User Accounts", Component: UserTypeComponent },
  { id: "userAccounts", label: "User Accounts", group: "User Accounts", Component: NewAccountForm },

  { id: "eventTypes", label: "Event Types", group: "Events", Component: EventType },
  { id: "eventTemplates", label: "Event Templates", group: "Events", Component: EventTemplateComponent },
  { id: "events", label: "Events", group: "Events", Component: EventsProgramComponent },
  { id: "locations", label: "Locations", group: "Events", Component: LocationComponent },

  { id: "ticketStatuses", label: "Ticket Statuses", group: "Tickets", Component: TicketStatusComponent },
  { id: "formStatuses", label: "Form Statuses", group: "Tickets", Component: RecordStatusComponent },

  { id: "siteContent", label: "Site Text / Wording", group: "Administration", Component: SiteContentComponent },
  { id: "permissions", label: "Permissions", group: "Administration", Component: PermissionComponent },
  { id: "actions", label: "Actions", group: "Administration", Component: ActionComponent },
  { id: "resources", label: "Resources", group: "Administration", Component: ResourceComponent },
];

function Management() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [activeModuleId, setActiveModuleId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [recentModuleIds, setRecentModuleIds] = useState([]);

  useEffect(() => {
    if (!authState) {
      navigate("/login");
    }
  }, [authState, navigate]);

  const filteredModules = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return MANAGEMENT_MODULES;
    return MANAGEMENT_MODULES.filter((module) => module.label.toLowerCase().includes(query));
  }, [searchQuery]);

  const groupedModules = useMemo(() => {
    return filteredModules.reduce((acc, module) => {
      if (!acc[module.group]) acc[module.group] = [];
      acc[module.group].push(module);
      return acc;
    }, {});
  }, [filteredModules]);

  const activeModule = useMemo(
    () => MANAGEMENT_MODULES.find((module) => module.id === activeModuleId) || null,
    [activeModuleId]
  );

  const handleModuleSelect = (moduleId) => {
    setActiveModuleId((prev) => (prev === moduleId ? null : moduleId));
    setRecentModuleIds((prev) => [moduleId, ...prev.filter((id) => id !== moduleId)].slice(0, 3));
  };

  const ActiveComponent = activeModule?.Component;
  const recentModules = recentModuleIds
    .map((id) => MANAGEMENT_MODULES.find((module) => module.id === id))
    .filter(Boolean);

  return (
    <div>
      <NavbarComponent />

      {authState ? (
        <Container fluid className="py-3">
          <div className="management-button-list mb-3">
            <Form.Group controlId="managementSearch" style={{ minWidth: "18rem" }}>
              <Form.Label className="fw-bold">Find Option</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search modules..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </Form.Group>
          </div>

          <div className="management-button-list">
            {Object.entries(groupedModules).map(([groupName, modules]) => (
              <DropdownButton key={groupName} id={`management-${groupName}`} title={groupName}>
                {modules.map((module) => (
                  <Dropdown.Item
                    as="button"
                    key={module.id}
                    onClick={() => handleModuleSelect(module.id)}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <span>{module.label}</span>
                    {activeModuleId === module.id && <Badge bg="primary">Active</Badge>}
                  </Dropdown.Item>
                ))}
              </DropdownButton>
            ))}
          </div>

          {recentModules.length > 0 && (
            <div className="d-flex flex-wrap gap-2 my-3 align-items-center">
              <span className="fw-bold">Recent:</span>
              {recentModules.map((module) => (
                <Button
                  key={module.id}
                  variant={activeModuleId === module.id ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => handleModuleSelect(module.id)}
                >
                  {module.label}
                </Button>
              ))}
            </div>
          )}

          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>{activeModule ? `Active: ${activeModule.label}` : "Management Workspace"}</strong>
              {activeModule && (
                <Button variant="outline-danger" size="sm" onClick={() => setActiveModuleId(null)}>
                  Close
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {ActiveComponent ? (
                <ActiveComponent />
              ) : (
                <Alert variant="info" className="mb-0">
                  Select a management option to open it here.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Container>
      ) : (
        <div>
          <h1>Please refresh the page and login!</h1>
        </div>
      )}
    </div>
  );
}

export default Management;
